// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import clsx from 'clsx';
import { mapObjectToArray } from './../../lib/objectArrayUtil';
import { ChoiceGroup, Dialog, DialogFooter, DialogType, IChoiceGroupOption } from 'office-ui-fabric-react';
import { getId } from 'office-ui-fabric-react/lib/Utilities';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingState } from './../../store/actionStatus';
import { GET_WIC_ADDRESS_DATA, resetWicAddressData, saveWicSelectedAddressData, wicAddressSelector } from './../../store/address';
import { selectedCountryInfoSelector } from './../../store/countries';
import { useStatusAndDataSelector } from './../../store/selectorHook';
import WalkInCenterSearch from './WalkInCenterSearch';
import Styles from './walkInCenterStyles.module.scss';


export interface WalkInCenterSearchPropType {

    hide: boolean;
    onClose: () => void;
}

const titleId: string = getId('dialogLabel');
const subTextId: string = getId('subTextLabel');

export default function WalkInCenterDialog(props: WalkInCenterSearchPropType) {

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [hide, setHide] = useState<boolean>(true);
    const [selectedWalkInCenter, setSelectedWalkInCenter] = useState<any>(undefined);

    useEffect(() => {
        setHide(props.hide);

        if (props.hide === false) {
            dispatch(resetWicAddressData());
        }
    }, [props.hide]);

    const { loadingState: walkInCentersLoadState, selectedData: walkInCenters } = useStatusAndDataSelector(wicAddressSelector, GET_WIC_ADDRESS_DATA);

    const countryInfo = useSelector(state => selectedCountryInfoSelector(state));
    const walkInCentersLoaded = walkInCentersLoadState.status === LoadingState.success;

    let dialogOptions: IChoiceGroupOption[] = [];
    if (walkInCentersLoaded) {
        dialogOptions = mapObjectToArray(walkInCenters).map((wic: any) => ({
            onRenderLabel,
            key: wic.locationId,
            text: wic.locationId,
        }));
    }

    function onCloseDialog() {
        setHide(true);
        props.onClose();
    }

    function onContinueClick() {
        const countryCode = countryInfo && countryInfo.countryCode;
        let wicAddress = {};
        if (selectedWalkInCenter) {
            wicAddress = {
                email: selectedWalkInCenter.emailAddress,
                address1: selectedWalkInCenter.addressLine1,
                address2: selectedWalkInCenter.addressLine2,
                city: selectedWalkInCenter.city,
                country: countryCode,
                phoneNumber: selectedWalkInCenter.phoneNumber,
                postalCode: selectedWalkInCenter.zipcode,
                stateOrProvince: selectedWalkInCenter.state,
                language: selectedWalkInCenter.locale,
            };
        }

        dispatch(saveWicSelectedAddressData(wicAddress));
        setHide(true);
        props.onClose();
    }

    function onChoiceChange(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) {
        if (option) {
            const key = option.key;
            const wic = walkInCenters[key];
            setSelectedWalkInCenter(wic);
        }
    }

    function onRenderLabel(option: IChoiceGroupOption): JSX.Element {

        const { text } = option;
        const wic = walkInCenters[text];
        const address = `${wic.addressLine1} ${wic.addressLine2 || ''}, ${wic.city}, ${wic.state} ${wic.zipcode}, ${wic.country}`;
        const phoneNumber = wic.phoneNumber;
        const hoursOfOperation = wic.hoursOfOperation;

        return wicOptionLabel({ address, phoneNumber, hoursOfOperation });
    }

    function wicOptionLabel({ address, phoneNumber, hoursOfOperation }: { address: any, phoneNumber: any, hoursOfOperation: any }): JSX.Element {
        return (<div className={Styles.optionContainer}>
            <div className={Styles.option}>
                <span className={Styles.optionLabel}>{`${t('address')}: `}</span>
                {address}</div>
            <div className={Styles.option}>
                <span className={Styles.optionLabel}>{`${t('phone_number')}: `}</span>
                {phoneNumber}
            </div>
            <div className={Styles.option}><span className={Styles.optionLabel}>{`${t('operation_hours')}: `}</span>
                {hoursOfOperation}
            </div>
        </div>);
    }

    return (
        <section>
            <Dialog
                hidden={hide}
                onDismiss={onCloseDialog}
                dialogContentProps={{
                    titleId,
                    subTextId,
                    closeButtonAriaLabel: t('close'),
                    onDismiss: onCloseDialog,
                    showCloseButton: true,
                    subText: t('please_enter_search_criterion'),
                    title: t('search_walk_in_center_address'),
                    type: DialogType.normal,
                }}
                modalProps={{
                    titleAriaId: titleId,
                    subtitleAriaId: subTextId,
                    isBlocking: true,
                    containerClassName: `${Styles.popup}`,
                }}
            >
                <WalkInCenterSearch />

                {
                    !walkInCentersLoaded
                        ? null
                        : dialogOptions.length > 0
                            ?
                            <ChoiceGroup
                                className="defaultChoiceGroup"
                                options={dialogOptions}
                                onChange={onChoiceChange}
                                label={t('please_select_one')}
                            />
                            :
                            t('no_walk_in_center_found')
                }

                <DialogFooter>
                    <button id="cancelBtn" className={clsx(Styles.buttonSecondary, Styles.button)} onClick={onCloseDialog} >{t('cancel')}</button>
                    <button id="continueBtn" className={clsx(Styles.buttonPrimary, Styles.button)} disabled={selectedWalkInCenter === undefined} onClick={onContinueClick}>{t('continue')}</button>
                </DialogFooter>
            </Dialog>
        </section>
    );
}
